import { Condition, Variable } from "generated/models";
import { capitalize, isEmpty, sortBy } from "lodash";
import sensorStatusIcon from "assets/svg/sensor-status.svg";
import gatewayStatusIcon from "assets/svg/gateway-status.svg";
import sensorValueIcon from "assets/svg/sensor-value.svg";
import recurrenceIcon from "assets/svg/recurrence.svg";
import dateIcon from "assets/svg/on-date.svg";
import evaluateIcon from "assets/svg/evaluate.svg";
import { ConditionInfos, DATE_MONTH_YEAR_FORMAT, DaysMap, EVENT_ENABLED_MODIFY_MESSAGE, EVENT_VARIABLE_TIMER_FORMAT, MonthsMap, TypeOption, variableType } from "constant";
import getEventInfo, { convertBEOperator } from "utils/eventFunctions";
import { Registry } from "types";
import HoverAuthorizeTooltip from "components/authorize/AuthorizeTooltip";
import { Link } from "react-router-dom";
import eventEditIcon from "assets/svg/event-edit.svg";
import { showErrorAlert } from "utils/alert";
import { MouseEvent } from "react";
import { convertSecondsToTimeString, formatValueWithUnit, getAccuracyFromOBJ, getExactAccuracy, getPrimaryUnit } from "utils/functions";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import { Id } from "react-toastify";
import moment from "moment";
import drag from "assets/svg/drag.svg";

type RepeatType = "daily" | "weekly" | "monthly" | "yearly";

type GenericMap = Record<string, unknown>;

type DeviceResourceShape = {
    for_?: number | null;
    gateway_id?: string;
    device_id?: string;
    said?: string;
    operator?: string | null;
    edge_trigger?: boolean;
    status?: string | null;
    thresholds?: string[] | null;
    threshold?: string;
    variable?: string;
    trigger_once?: boolean;
};

type DateTimeShape = {
    ranges?: unknown;
    points?: unknown;
    schedule?: string;
    interval?: string;
};

type Date2Shape = {
    repeat?: RepeatType | "";
    weekly?: { weekdays?: GenericMap };
    monthly?: { weekdays?: GenericMap; days?: GenericMap };
    yearly?: {
        months?: Record<
            string,
            {
                days?: GenericMap;
                weekdays?: GenericMap;
            }
        > | null;
    };
    [key: string]: unknown;
};

type ConditionResourceShape = {
    device?: DeviceResourceShape;
    date1?: { day?: string; time_?: DateTimeShape };
    date2?: Date2Shape;
};

type RenderConditionItem = Condition & {
    type_?: string;
    resource?: ConditionResourceShape;
};

const getDaySuffix = (day: string) => {
    if (["1", "21", "31"].includes(day)) return "st";
    if (["2", "22"].includes(day)) return "nd";
    if (["3", "23"].includes(day)) return "rd";
    return "th";
};

const normalizeUnit = (unit: string) => {
    if (unit === "C") {
        return "°C";
    }
    if (unit === "na") {
        return "";
    }
    return unit;
};

const SensorValueText: Record<string, string> = {
    ">": "Is above {formattedValue}",
    "<": "Is below {formattedValue}",
    ">=": "Rises above {formattedValue}",
    "<=": "Falls below {formattedValue}",
    "==": "Is equal to {value}",
    "===": "Changes to {value}",
    in: "Is within a range of {formattedMin} to {formattedMax}",
    "not in": "Is outside a range of {formattedMin} to {formattedMax}",
    "in=": "Enters into a range of {formattedMin} to {formattedMax}",
    "not in=": "Exits from a range of {formattedMin} to {formattedMax}",
};

const getLdsuNameByDeviceId = (allGateways: Registry[], deviceId: string) => {
    for (const gateway of allGateways) {
        if (!gateway.configs) {
            continue;
        }
        const ldsu = gateway.configs.find((item: any) => item.UID === deviceId);
        if (ldsu?.name) {
            return ldsu.name;
        }
    }
    return "";
};

const getGatewayNameById = (allGateways: Registry[], gatewayId: string) => {
    for (const gateway of allGateways) {
        if (gateway.gateway_id === gatewayId) {
            return gateway.name;
        }
    }
    return "";
};

const getSensorDisplayData = ({
    allGateways,
    metricCLSList,
    jsonList,
    gatewayId,
    deviceId,
    said,
    operator,
    edgeTrigger,
    thresholds,
}: {
    allGateways: Registry[];
    metricCLSList: Record<string, { name?: string }>;
    jsonList: any[];
    gatewayId: string;
    deviceId: string;
    said: string;
    operator: string | null;
    edgeTrigger: boolean;
    thresholds: string[] | null;
}) => {
    let sensorName = "";
    let sensorType = "";
    let selectedSensor: any;
    let deviceOBJ = "";

    for (const gateway of allGateways) {
        if (gateway.gateway_id === gatewayId && gateway.info?.devices) {
            const sensor = gateway.info.devices.find(
                (item: any) => item.UID === deviceId
            );
            if (sensor) {
                selectedSensor = sensor;
                deviceOBJ = sensor.OBJ || "";
            }
        }

        if (gateway.configs?.length) {
            const configuredSensor = gateway.configs.find(
                (item: any) => item.UID === deviceId
            );
            const sensorNames = (configuredSensor as any)?.sensor_names;
            if (sensorNames?.[said]) {
                sensorName = sensorNames[said];
            }
        }
    }

    if (!sensorName) {
        return { sensorName: "", sensorType: "", message: "" };
    }

    const appId = selectedSensor?.APP?.[said]?.APPID;
    const accuracy = getAccuracyFromOBJ({
        object: deviceOBJ,
        jsonList,
        said,
        appId,
        isChart: false,
    });
    const accuracyException = getExactAccuracy(accuracy);

    const selectedJsonSensor = jsonList.find((item: any) => item.OBJ === deviceOBJ);
    const rawUnit = selectedSensor?.APP?.length
        ? getPrimaryUnit(selectedSensor.APP[said])
        : selectedJsonSensor?.SNS?.[said]?.UNIT;
    const cls = selectedJsonSensor?.SNS?.[said]?.CLS;
    sensorType = selectedSensor?.APP?.[said]?.NAME || metricCLSList?.[cls]?.name || "";
    const unit = normalizeUnit(rawUnit || "");

    const [minThreshold = "0", maxThreshold = ""] = thresholds || [];
    const value = Number(minThreshold).toFixed(accuracyException);
    const operatorKey = convertBEOperator(operator ?? "", edgeTrigger);

    let message = SensorValueText[operatorKey]?.fill({
        value,
        formattedValue: formatValueWithUnit(value, unit),
    });

    if (maxThreshold) {
        const min = Number(minThreshold).toFixed(accuracyException);
        const max = Number(maxThreshold).toFixed(accuracyException);
        message = SensorValueText[operatorKey]?.fill({
            min,
            max,
            formattedMin: formatValueWithUnit(min, unit),
            formattedMax: formatValueWithUnit(max, unit),
        });
    }

    return {
        sensorName,
        sensorType,
        message: message || "",
    };
};

const getCommonValue = (variableItem: any, threshold: string) => {
    if (variableItem?.type_ === TypeOption.Timer) {
        return convertSecondsToTimeString(
            +threshold,
            EVENT_VARIABLE_TIMER_FORMAT
        );
    }

    if (variableItem?.type_ === TypeOption.Boolean) {
        return threshold;
    }

    if (variableItem?.type_ === TypeOption.Float) {
        return Number(threshold).toFixed(2);
    }

    if (variableItem?.type_ === TypeOption.Counter) {
        if (+threshold === +variableItem?.attributes?.terminal) {
            return "Terminal Value";
        } else if (+threshold === +variableItem?.initial) {
            return "Initial Value";
        }
    }

    return Number(threshold);
};

const renderMissingEntityWarning = (entity: string) => {
    return (
        <span className="warning-message">
            <i className="material-icons outlined">error_outline</i>
            <span>{`This ${entity} is not found`}</span>
        </span>
    );
};

const formatWeeklyRecurrence = (weeklyWeekdays: GenericMap) => {
    return Object.keys(weeklyWeekdays)
        .filter((day) => Number(day) >= 1 && Number(day) <= 7)
        .sort((a, b) => Number(a) - Number(b))
        .map((day) => DaysMap[Number(day)])
        .join(", ");
};

const formatMonthlyRecurrence = (monthlyDays: GenericMap, monthlyWeekdays: GenericMap) => {
    const formattedDays: string[] = [];
    const formattedWeekdays: string[] = [];

    if (!isEmpty(monthlyDays)) {
        formattedDays.push(
            ...Object.keys(monthlyDays).map((day) =>
                day === "-1" ? "Last day" : `${day}${getDaySuffix(day)}`
            )
        );
    }

    if (!isEmpty(monthlyWeekdays)) {
        formattedWeekdays.push(
            ...Object.keys(monthlyWeekdays).map((weekday) => {
                if (Number(weekday) > 0) {
                    return `First ${DaysMap[Number(weekday)]}`;
                }
                return `Last ${DaysMap[Number(weekday) * -1]}`;
            })
        );
    }

    return `On ${formattedDays.concat(formattedWeekdays).join(", ")}`;
};

const formatYearlyRecurrence = (
    months: Record<string, { days?: GenericMap; weekdays?: GenericMap }> | null
) => {
    const formattedDays: string[] = [];
    const formattedWeekdays: string[] = [];

    if (!months) {
        return "On ";
    }

    for (const month in months) {
        if (!isEmpty(months[month].days)) {
            formattedDays.push(
                ...Object.keys(months[month].days || {}).map((day) => {
                    if (day === "-1") {
                        return `Last day of ${MonthsMap[Number(month)]}`;
                    }
                    return `${MonthsMap[Number(month)]} ${day}${getDaySuffix(day)}`;
                })
            );
        }

        if (!isEmpty(months[month].weekdays)) {
            formattedWeekdays.push(
                ...Object.keys(months[month].weekdays || {}).map((weekday) => {
                    if (Number(weekday) > 0) {
                        return `First ${DaysMap[Number(weekday)]} of ${MonthsMap[Number(month)]}`;
                    }
                    return `Last ${DaysMap[Number(weekday) * -1]} of ${MonthsMap[Number(month)]}`;
                })
            );
        }
    }

    return `On ${formattedDays.concat(formattedWeekdays).join(", ")}`;
};

const CONDITION_SENSOR_VALUE = "sensor-value";
const CONDITION_LDSU_STATUS = "ldsu-status";
const CONDITION_GATEWAY_STATUS = "gateway-status";
const CONDITION_RECURRENCE = "recurrence";
const CONDITION_ONDATE = "on-date";
const CONDITION_VARIABLE = "event-variable";
const CONDITION = "condition";
const spacePlaceHolder = "　";

type ConditionSlotItemProps = {
    conditions: Condition[],
    allGateways: Registry[],
    isGatewayLevel: boolean,
    eventId: string,
    isEventActivated: boolean,
    eventVariables: Variable[],
    jsonList: any[],
    metricCLSList: Record<string, {
        name: string;
        metric: string;
    }>,
    showDeleteConfirmation: (type: string, uuid: string) => Id | undefined;
}

export const ConditionSlotItem = (props: ConditionSlotItemProps) => {
    const { conditions,
        allGateways,
        isGatewayLevel,
        eventId,
        isEventActivated,
        eventVariables,
        jsonList,
        metricCLSList,
        showDeleteConfirmation } = props;

    const checkModify = (
        e?: MouseEvent<HTMLAnchorElement, globalThis.MouseEvent>
    ) => {
        if (isEventActivated) {
            if (e) {
                e.preventDefault();
                e.stopPropagation();
            }

            showErrorAlert({
                message: EVENT_ENABLED_MODIFY_MESSAGE,
            });
            return false;
        }

        return true;
    };

    const renderEditCondition = (
        conditionType: string,
        id: string,
        conditionPayload?: any
    ) => (
        <HoverAuthorizeTooltip permission="event:update" className="event-edit">
            <Link
                to={{
                    pathname: `/event-details/${eventId}/condition/${conditionType}/${id}`,
                    state: {
                        ...(conditionPayload
                            ? { conditionPayload: conditionPayload }
                            : {}),
                        isGatewayLevel,
                    },
                }}
                onClick={checkModify}
            >
                <img
                    width={19}
                    height={19}
                    src={eventEditIcon}
                    alt="Edit condition or event"
                />
            </Link>
        </HoverAuthorizeTooltip>
    );

    const getDateTimeStatement = ({
        repeat,
        repeatKey,
        time1,
        repeatTime,
        weeklyWeekdays,
        monthlyDays,
        monthlyWeekdays,
        months,
    }: {
        repeat: string;
        repeatKey: RepeatType;
        time1: DateTimeShape;
        repeatTime: DateTimeShape;
        weeklyWeekdays: GenericMap;
        monthlyDays: GenericMap;
        monthlyWeekdays: GenericMap;
        months: Record<string, { days?: GenericMap; weekdays?: GenericMap }> | null;
    }) => {
        if (!repeat) {
            return getEventInfo(
                time1.schedule ?? "",
                time1.ranges || null,
                time1.points || null,
                time1.interval ?? ""
            );
        }

        switch (repeatKey) {
            case "daily":
                return getEventInfo(
                    repeatTime.schedule ?? "",
                    repeatTime.ranges || null,
                    repeatTime.points || null,
                    repeatTime.interval ?? ""
                );
            case "weekly":
                return formatWeeklyRecurrence(weeklyWeekdays);
            case "monthly":
                return formatMonthlyRecurrence(monthlyDays, monthlyWeekdays);
            case "yearly":
                return formatYearlyRecurrence(months);
            default:
                return null;
        }
    };

    const renderLdsuContent = (
        uuid: string,
        deviceId: string,
        status: string | null,
        forDuration: number | null
    ) => {
        const ldsuName = getLdsuNameByDeviceId(allGateways, deviceId);
        if (!ldsuName) {
            return renderMissingEntityWarning("LDSU");
        }

        return (
            <>
                {ldsuName}
                {renderEditCondition(CONDITION_LDSU_STATUS, uuid)}
                <p>LDSU UUID: {deviceId}</p>
                <h6>
                    {status === "online" ? "Comes" : "Goes"} {status} for{" "}
                    {forDuration} sec(s)
                </h6>
            </>
        );
    };

    const renderGatewayContent = (
        uuid: string,
        gatewayId: string,
        status: string | null,
        forDuration: number | null
    ) => {
        const gatewayName = getGatewayNameById(allGateways, gatewayId);
        if (!gatewayName) {
            return renderMissingEntityWarning("Gateway");
        }

        return (
            <>
                {gatewayName || spacePlaceHolder}
                {renderEditCondition(CONDITION_GATEWAY_STATUS, uuid)}
                <p>UUID: {gatewayId}</p>
                <h6>
                    {status === "online" ? "Comes" : "Goes"} {status} for{" "}
                    {forDuration} sec(s)
                </h6>
            </>
        );
    };

    const renderSensorContent = ({
        uuid,
        gatewayId,
        deviceId,
        said,
        operator,
        edgeTrigger,
        thresholds,
        forDuration,
    }: {
        uuid: string;
        gatewayId: string;
        deviceId: string;
        said: string;
        operator: string | null;
        edgeTrigger: boolean;
        thresholds: string[] | null;
        forDuration: number | null;
    }) => {
        const sensorData = getSensorDisplayData({
            allGateways,
            metricCLSList,
            jsonList,
            gatewayId,
            deviceId,
            said,
            operator,
            edgeTrigger,
            thresholds,
        });
        if (!sensorData.sensorName) {
            return renderMissingEntityWarning("Sensor");
        }

        return (
            <>
                {sensorData.sensorName}
                {renderEditCondition(CONDITION_SENSOR_VALUE, uuid)}
                <p>LDSU UUID: {deviceId}</p>
                <p>{sensorData.sensorType || spacePlaceHolder}</p>
                <h6>{sensorData.message} and remains for {forDuration} sec(s)</h6>
            </>
        );
    };

    const renderVariableContent = ({
        uuid,
        variable,
        operator,
        threshold,
        trigger_once,
        slot,
    }: {
        uuid: string;
        variable: string;
        operator: string | null;
        threshold: string;
        trigger_once: boolean;
        slot: number | null | undefined;
    }) => {
        const variableItem: any = eventVariables.find(
            ({ uuid }) => uuid === variable
        );
        const triggerOnce = trigger_once ? "when" : "is";
        const operatorConst = `${triggerOnce}-${operator}`;

        return (
            <>
                {renderEditCondition(CONDITION_VARIABLE, variableItem?.uuid, {
                    operator,
                    variable,
                    threshold,
                    uuid,
                    slot,
                    trigger_once,
                })}
                <h6>{variableItem?.name || ""}</h6>
                <p>
                    Event Variable -{" "}
                    {variableItem?.type_?.toLowerCase()?.capitalize() || ""}
                </p>
                <h6>
                    {(variableItem?.type_ as TypeOption) === TypeOption.Boolean
                        ? triggerOnce.capitalize()
                        : ConditionInfos[operatorConst]?.label || ""}{" "}
                    {getCommonValue(variableItem, threshold)}
                </h6>
            </>
        );
    };

    const renderDatetimeContent = ({
        uuid,
        day,
        repeat,
        dateTimeStatement,
    }: {
        uuid: string;
        day: string;
        repeat: string;
        dateTimeStatement: JSX.Element | string | null | undefined;
    }) => {
        if (!repeat) {
            return (
                <>
                    <h6>On {moment(day).format(DATE_MONTH_YEAR_FORMAT)}</h6>
                    {renderEditCondition(CONDITION_ONDATE, uuid)}
                    {dateTimeStatement}
                </>
            );
        }

        return (
            <>
                <h6>{capitalize(repeat)}</h6>
                {renderEditCondition(CONDITION_RECURRENCE, uuid)}
                <p>Recurrence</p>
                {dateTimeStatement}
            </>
        );
    };

    const list = sortBy(conditions, "order").map((condition: Condition) => {
        const { uuid, type_, resource } = condition;

        const renderConditionIcon = () => {
            switch (type_) {
                case "LDSU":
                    return <img src={sensorStatusIcon} alt="" />;
                case "GATEWAY":
                    return <img src={gatewayStatusIcon} alt="" />;
                case "SENSOR":
                    return <img src={sensorValueIcon} alt="" />;
                case "DATETIME":
                    if (resource.date2?.repeat)
                        return <img src={recurrenceIcon} alt="" />;
                    return <img src={dateIcon} alt="" />;
                case variableType:
                    return <img src={evaluateIcon} alt="" />;
            }
        };

        const renderConditionContent = () => {
            const currentCondition = condition as RenderConditionItem;
            const conditionType = currentCondition.type_ || "";
            const resourceData: ConditionResourceShape =
                currentCondition.resource || {};
            const deviceData: DeviceResourceShape =
                resourceData.device || {};
            const date1: NonNullable<ConditionResourceShape["date1"]> =
                resourceData.date1 || {};
            const date2: Date2Shape = resourceData.date2 || {};

            const {
                for_: forDuration = null,
                gateway_id = "",
                device_id = "",
                said = "",
                operator = null,
                edge_trigger = false,
                status = null,
                thresholds = null,
                threshold = "",
                variable = "",
                trigger_once = true,
            } = deviceData;
            const day = date1.day ?? "";
            const time1 = date1.time_ || {};
            const repeat = date2.repeat ?? "";
            const repeatKey = repeat as RepeatType;
            const repeatTime = (date2[repeatKey] as DateTimeShape) || {};
            const weeklyWeekdays = date2.weekly?.weekdays || {};
            const monthlyWeekdays = date2.monthly?.weekdays || {};
            const monthlyDays = date2.monthly?.days || {};
            const months = date2.yearly?.months || null;
            const dateTimeStatement = getDateTimeStatement({
                repeat,
                repeatKey,
                time1,
                repeatTime,
                weeklyWeekdays,
                monthlyDays,
                monthlyWeekdays,
                months,
            });

            switch (conditionType) {
                case "LDSU":
                    return renderLdsuContent(uuid, device_id, status, forDuration);
                case "GATEWAY":
                    return renderGatewayContent(
                        uuid,
                        gateway_id,
                        status,
                        forDuration
                    );
                case "SENSOR":
                    return renderSensorContent({
                        uuid,
                        gatewayId: gateway_id,
                        deviceId: device_id,
                        said,
                        operator,
                        edgeTrigger: edge_trigger,
                        thresholds,
                        forDuration,
                    });
                case variableType:
                    return renderVariableContent({
                        uuid,
                        variable,
                        operator,
                        threshold,
                        trigger_once,
                        slot: condition.slot,
                    });
                case "DATETIME":
                    return renderDatetimeContent({
                        uuid,
                        day,
                        repeat,
                        dateTimeStatement,
                    });
                default:
                    return null;
            }
        };

        return (
            <div
                className="grid-stack-item"
                id={`gs-${uuid}`}
                key={`condition-${uuid}`}
                {...{ "gs-w": "12", "gs-no-resize": "true" }}
            >
                <div className="condition-item">
                    <div className="condition-box">
                        {renderConditionIcon()}

                        <div className="condition-texts">
                            {renderConditionContent()}
                        </div>
                        <HoverAuthorizeTooltip
                            permission="event:update"
                            className="drag-action"
                        >
                            <img
                                className="grid-stack-item-content"
                                width={24}
                                height={24}
                                src={drag}
                                alt="drag"
                            />
                        </HoverAuthorizeTooltip>
                    </div>
                    <HoverAuthorizeTooltip
                        permission="event:delete"
                        className="remove-action"
                    >
                        <CloseOutlined
                            aria-label="delete-condition"
                            style={{ color: "#f2495e", cursor: "pointer" }}
                            onClick={() =>
                                showDeleteConfirmation(CONDITION, uuid)
                            }
                            onKeyDown={() =>
                                showDeleteConfirmation(CONDITION, uuid)
                            }
                        />
                    </HoverAuthorizeTooltip>
                    <div className="and-section">
                        <div className="and-btn">AND</div>
                    </div>
                </div>
            </div>
        );
    });
    return <>{list}</>;
};